import type { ParsedCSVRow, CSVValidationError } from '../types/asset-import.types';

/**
 * Formats a number as currency (GBP)
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value);
}

/**
 * Validates a file before processing
 * Checks file type and size
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  // Check file type
  if (!file.name.endsWith('.csv')) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload a CSV file.',
    };
  }

  // Check file size (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File size exceeds 10MB limit.',
    };
  }

  return { valid: true };
}

/**
 * Validates an array of asset import rows
 * Returns an object with valid rows and errors
 */
export function validateAssets(rows: ParsedCSVRow[]): {
  validRows: ParsedCSVRow[];
  errors: CSVValidationError[];
} {
  const validRows: ParsedCSVRow[] = [];
  const errors: CSVValidationError[] = [];

  rows.forEach((row, index) => {
    const rowNumber = index + 2; // +2 because row 1 is header, and we're 0-indexed
    const rowErrors: string[] = [];

    // Validate Asset Code
    if (!row.assetCode || row.assetCode.trim() === '') {
      rowErrors.push('Asset code is required');
    }

    // Validate Description
    if (!row.description || row.description.trim() === '') {
      rowErrors.push('Description is required');
    }

    // Validate Category
    if (!row.category || row.category.trim() === '') {
      rowErrors.push('Category is required');
    }

    // Validate Bought Value
    if (!row.boughtValue) {
      rowErrors.push('Bought value is required');
    } else {
      const value = parseFloat(row.boughtValue.toString().replace(/[£,]/g, ''));
      if (isNaN(value)) {
        rowErrors.push('Invalid number format for bought value');
      } else if (value <= 0) {
        rowErrors.push('Bought value must be greater than zero');
      }
    }

    // Validate Bought Date
    if (!row.boughtDate) {
      rowErrors.push('Bought date is required');
    } else {
      const date = parseDateString(row.boughtDate);
      if (!date || isNaN(date.getTime())) {
        rowErrors.push('Invalid date format. Use DD/MM/YYYY');
      }
    }

    // If there are errors for this row, add them to the errors array
    if (rowErrors.length > 0) {
      rowErrors.forEach(error => {
        errors.push({
          row: rowNumber,
          field: getFieldFromError(error),
          error: error,
        });
      });
    } else {
      // Row is valid
      validRows.push(row);
    }
  });

  return { validRows, errors };
}

/**
 * Parse a date string in DD/MM/YYYY format
 */
function parseDateString(dateStr: string): Date | null {
  if (!dateStr) return null;

  // Try DD/MM/YYYY format
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
    const year = parseInt(parts[2], 10);
    
    const date = new Date(year, month, day);
    
    // Validate the date is real
    if (
      date.getDate() === day &&
      date.getMonth() === month &&
      date.getFullYear() === year
    ) {
      return date;
    }
  }

  // Try ISO format as fallback
  const isoDate = new Date(dateStr);
  if (!isNaN(isoDate.getTime())) {
    return isoDate;
  }

  return null;
}

/**
 * Extract field name from error message
 */
function getFieldFromError(error: string): string {
  if (error.includes('Asset code') || error.includes('asset code')) {
    return 'Asset Code';
  }
  if (error.includes('Description') || error.includes('description')) {
    return 'Description';
  }
  if (error.includes('Category') || error.includes('category')) {
    return 'Category';
  }
  if (error.includes('Bought value') || error.includes('bought value') || error.includes('value')) {
    return 'Bought Value';
  }
  if (error.includes('Bought date') || error.includes('bought date') || error.includes('date')) {
    return 'Bought Date';
  }
  if (error.includes('Supplier') || error.includes('supplier')) {
    return 'Supplier';
  }
  if (error.includes('Serial') || error.includes('serial')) {
    return 'Serial Number';
  }
  return 'Unknown';
}

/**
 * Validate a single asset row
 */
export function validateAssetRow(row: ParsedCSVRow): string[] {
  const errors: string[] = [];

  if (!row.assetCode || row.assetCode.trim() === '') {
    errors.push('Asset code is required');
  }

  if (!row.description || row.description.trim() === '') {
    errors.push('Description is required');
  }

  if (!row.category || row.category.trim() === '') {
    errors.push('Category is required');
  }

  if (!row.boughtValue) {
    errors.push('Bought value is required');
  } else {
    const value = parseFloat(row.boughtValue.toString().replace(/[£,]/g, ''));
    if (isNaN(value)) {
      errors.push('Invalid number format for bought value');
    } else if (value <= 0) {
      errors.push('Bought value must be greater than zero');
    }
  }

  if (!row.boughtDate) {
    errors.push('Bought date is required');
  } else {
    const date = parseDateString(row.boughtDate);
    if (!date || isNaN(date.getTime())) {
      errors.push('Invalid date format. Use DD/MM/YYYY');
    }
  }

  return errors;
}
